import React, { createContext, useState } from "react";
import jwt_decode from "jwt-decode";
import { pick } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import useToastContext from "hooks/useToastContext";
import { apiPost, apiGet } from "../utils/apiFetch";
import apiPath from "../utils/apiPath";
const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  let [user, setUser] = useState(() => {
    const token = localStorage.getItem("token");
    // PREVIOUS: leftover `user` in localStorage after a 401 kept HRRoutes
    // mounted and bounced /hr-login → /dashboard, flooding 401s with no token.
    if (!token) return null;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        return jwt_decode(token);
      }
    }
    return jwt_decode(token);
  });
  const [sidebarStatus, setSidebarStatus] = useState(
    localStorage.getItem("sidebar"),
  );
  const [resetPasswordData, setResetPasswordData] = useState({});
  const [pageName, setPageName] = useState(
    localStorage.getItem("pageName")
      ? localStorage.getItem("pageName")
      : "Dashboard",
  );
  // Add state for profile completion data
  const [profileCompletion, setProfileCompletion] = useState(null);
  // --- MFA: tracks an in-progress login that is waiting on a TOTP code ---
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const navigate = useNavigate();
  const notification = useToastContext();
  const [loginMessage] = useState("Admin details updated successfully.");

  const updatePageName = (name) => {
    localStorage.setItem("pageName", name);
    setPageName(name);
  };

  // Function to fetch profile completion status based on user role
  const fetchProfileCompletionStatus = async (userRole, userId) => {
    try {
      let completionApi = "";

      // Skip for admin users
      if (userRole === "admin" || userRole === "subadmin") {
        setProfileCompletion({
          skip: true,
          message: "Admin user - no completion check needed",
        });
        return;
      }

      // Determine the correct API endpoint based on user role
      switch (userRole) {
        case "HRAdmin":
          completionApi = apiPath.hrCompletionStatus;
          break;
        case "AccountantAdmin":
          completionApi = apiPath.accountantCompletionStatus;
          break;
        default:
          console.warn(`No completion API defined for role: ${userRole}`);
          return;
      }

      if (completionApi) {
        const { status, data } = await apiGet(completionApi);
        if (status === 200 && data.success) {
          setProfileCompletion({
            ...data.results,
            role: userRole,
            lastChecked: new Date().toISOString(),
          });
        } else {
          console.error(
            "Failed to fetch profile completion status:",
            data.message,
          );
          setProfileCompletion({
            error: true,
            message: data.message || "Failed to fetch completion status",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching profile completion status:", error);
      setProfileCompletion({
        error: true,
        message: "Error fetching completion status",
      });
    }
  };

  // Shared by loginUser() and completeMfaLogin() so both paths finish
  // login identically once a real token exists.
  const finalizeLogin = async (data) => {
    const token = data?.results?.token || null;
    const refresh_token = data?.results?.refresh_token || null;
    localStorage.setItem("token", token);
    localStorage.setItem("refresh_token", refresh_token);
    localStorage.setItem("sidebar", "open");
    setSidebarStatus("open");
    localStorage.setItem("pageName", "Dashboard");
    setPageName("Dashboard");

    // Store full user data from results, merged with the decoded token
    const fullUserData = { ...data.results };
    delete fullUserData.token;
    delete fullUserData.refresh_token;
    delete fullUserData.recoveryCodes;

    const decodedUser = jwt_decode(token);
    const mergedUser = { ...decodedUser, ...fullUserData };

    localStorage.setItem("user", JSON.stringify(mergedUser));
    setUser(mergedUser); // single state update

    if (decodedUser.role !== "admin" && decodedUser.role !== "subadmin") {
      await fetchProfileCompletionStatus(decodedUser.role, decodedUser._id);
    } else {
      setProfileCompletion({
        skip: true,
        message: "Admin user - no completion check needed",
      });
    }

    notification.success(data.message);

    // The redirect / temporary-password popup is handled HERE (not in Login.jsx)
    // because the Login component can be unmounted by the route guard as soon
    // as `user` is set, so an effect inside Login would never run.
    if (mergedUser.isTemporaryPassword === true) {
      // Intentionally not awaited so loginUser() can still hide the loader.
      Swal.fire({
        icon: "warning",
        title: "Change Password",
        text: "You are using a temporary password. Please change it now to secure your account.",
        confirmButtonText: "Change Password",
        cancelButtonText: "Later",
        showCancelButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        reverseButtons: true,
        confirmButtonColor: "#6236FF",
        cancelButtonColor: "#A5A5A5",
      }).then((result) => {
        navigate(result.isConfirmed ? "/change-password" : "/dashboard");
      });
    } else {
      navigate("/dashboard");
    }
  };

  let loginUser = async (body) => {
    let api = "";
    if (location.pathname === "/accountant-login") {
      api = apiPath.loginAccountant;
    } else if (location.pathname === "/hr-login") {
      api = apiPath.loginHR;
    } else {
      api = apiPath.loginUser;
    }

    document.getElementById("loader").classList.remove("hidden");
    try {
      const { status, data } = await apiPost(
        api,
        pick(body, [
          "email",
          "password",
          "latitude",
          "longitude",
          "loginAddress",
          "loginIp",
          "deviceId",
        ]),
      );

      if (status === 200) {
        if (data.success) {
          // --- MFA: password was correct, but this account has MFA enabled.
          if (data.results?.mfaRequired || data.results?.mfaSetupRequired) {
            setMfaChallenge({
              setup: !!data.results.mfaSetupRequired,
              preAuthToken: data.results.preAuthToken,
              role: data.results.role,
              qrCodeDataUrl: data.results.qrCodeDataUrl,
              manualEntryKey: data.results.manualEntryKey,
            });
            document.getElementById("loader").classList.add("hidden");
            return;
          }

          await finalizeLogin(data);
        } else {
          notification.error(
            data?.message ||
              "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP.",
          );
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      notification.error(
        data?.message ||
          "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP.",
      );
    }
    document.getElementById("loader").classList.add("hidden");
  };

  // --- MFA: called by the Authenticator modal's onVerify prop during login.
  const verifyLoginMfa = async (code, { isRecoveryCode } = {}) => {
    let loginGps = {};
    try {
      loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
    } catch (err) {
      loginGps = {};
    }
    const body = isRecoveryCode
      ? {
          preAuthToken: mfaChallenge?.preAuthToken,
          recoveryCode: code,
          role: mfaChallenge?.role,
          ...loginGps,
        }
      : {
          preAuthToken: mfaChallenge?.preAuthToken,
          token: code,
          role: mfaChallenge?.role,
          ...loginGps,
        };

    const { data } = await apiPost(apiPath.mfaLoginVerify, body);

    if (data?.success) {
      return { verified: true, __loginData: data };
    }

    // Backend may issue a fresh preAuthToken
    if (data?.results?.preAuthToken) {
      setMfaChallenge((prev) => ({
        ...prev,
        preAuthToken: data.results.preAuthToken,
      }));
    }
    return { verified: false, message: data?.message };
  };

  // --- MFA: called by Authenticator's onSuccess once verifyLoginMfa()
  const completeMfaLogin = async (result) => {
    setMfaChallenge(null);
    await finalizeLogin(result.__loginData);
  };

  const completeMfaSetupLogin = async (code) => {
    const { data } = await apiPost(apiPath.mfaLoginSetupVerify, {
      preAuthToken: mfaChallenge?.preAuthToken,
      token: code,
      role: mfaChallenge?.role,
    });
    if (data?.success) {
      setMfaChallenge((prev) => ({
        ...prev,
        pendingLogin: data,
      }));
      return {
        verified: true,
        recoveryCodes: data?.results?.recoveryCodes || [],
      };
    }
    if (data?.results?.preAuthToken || data?.data?.preAuthToken) {
      setMfaChallenge((prev) => ({
        ...prev,
        preAuthToken: data.results?.preAuthToken || data.data?.preAuthToken,
      }));
    }
    return { verified: false, message: data?.message };
  };

  const finishMfaSetupLogin = async () => {
    const data = mfaChallenge?.pendingLogin;
    setMfaChallenge(null);
    if (data?.success) await finalizeLogin(data);
  };

  const cancelMfaLogin = () => {
    setMfaChallenge(null);
  };

  const updateProfile = async (
    formData,
    formDataHR,
    formDataAccountant,
    setPicture,
  ) => {
    let api = "";
    if (user.role === "admin" || user.role === "subadmin") {
      api = apiPath.editProfile;
    } else if (user.role === "HRAdmin") {
      api = apiPath.updateProfileHR;
    } else {
      api = apiPath.updateProfileAccountant;
    }
    let ObjData = "";
    if (user.role === "admin" || user.role === "subadmin") {
      ObjData = formData;
    } else if (user.role === "HRAdmin") {
      ObjData = formDataHR;
    } else {
      ObjData = formDataAccountant;
    }
    try {
      const res = await apiPost(api, ObjData);
      const token = res?.data?.results?.token || null;
      const refresh_token = res?.data?.results?.refresh_token || null;
      localStorage.setItem("token", token);
      notification.success(res?.data?.message);
      localStorage.setItem("refresh_token", refresh_token);

      // Update full user data after profile update
      const fullUserData = { ...res.data.results };
      delete fullUserData.token;
      delete fullUserData.refresh_token;

      localStorage.setItem("user", JSON.stringify(fullUserData));
      setUser(fullUserData);

      // Refresh profile completion status after profile update
      if (fullUserData.role !== "admin" && fullUserData.role !== "subadmin") {
        await fetchProfileCompletionStatus(fullUserData.role, fullUserData._id);
      }

      const { message, success } = res.data;
      if (res && success) {
        notification.success(message);
        setPicture({
          cropperOpen: false,
          img: null,
          zoom: 2,
          croppedImg:
            res?.data?.results?.profilePic ||
            res?.data?.results?.profilePicture,
        });
      }
    } catch (err) {
      console.log("err:", err);
    }
  };

  // Function to manually refresh completion status
  const refreshCompletionStatus = async () => {
    if (user && user.role !== "admin" && user.role !== "subadmin") {
      await fetchProfileCompletionStatus(user.role, user._id);
    }
  };

  const handleSidebar = () => {
    let s1 = localStorage.getItem("sidebar");
    localStorage.setItem("sidebar", s1 == "open" ? "close" : "open");
    setSidebarStatus(s1 == "open" ? "close" : "open");
  };

  let logoutUser = () => {
    let loginPath = "/login"; // default for admin/subadmin

    if (user?.role === "HRAdmin") {
      loginPath = "/hr-login";
    } else if (user?.role === "AccountantAdmin") {
      loginPath = "/accountant-login";
    } else if (user?.role === "admin" || user?.role === "subadmin") {
      loginPath = "/login";
    }

    setUser(null);
    setProfileCompletion(null);
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    notification.success("Logout successfully.");
    navigate(loginPath);
  };

  let contextData = {
    user,
    loginUser: loginUser,
    logoutUser: logoutUser,
    updateProfile: updateProfile,
    sidebarStatus: sidebarStatus,
    handleSidebar,
    pageName,
    updatePageName,
    loginMessage: loginMessage,
    setResetPasswordData,
    resetPasswordData,
    // Add profile completion data and refresh function to context
    profileCompletion,
    refreshCompletionStatus,
    // --- MFA additions ---
    mfaChallenge,
    verifyLoginMfa,
    completeMfaLogin,
    completeMfaSetupLogin,
    finishMfaSetupLogin,
    cancelMfaLogin,
  };

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};




// import React, { createContext, useState } from "react";
// import jwt_decode from "jwt-decode";
// import { pick } from "lodash";
// import { useLocation, useNavigate } from "react-router-dom";
// import useToastContext from "hooks/useToastContext";
// import { apiPost, apiGet } from "../utils/apiFetch";
// import apiPath from "../utils/apiPath";
// const AuthContext = createContext();

// export default AuthContext;

// export const AuthProvider = ({ children }) => {
//   const location = useLocation();
//   let [user, setUser] = useState(() => {
//     const token = localStorage.getItem("token");
//     // PREVIOUS: leftover `user` in localStorage after a 401 kept HRRoutes
//     // mounted and bounced /hr-login → /dashboard, flooding 401s with no token.
//     if (!token) return null;
//     const storedUser = localStorage.getItem("user");
//     if (storedUser) {
//       try {
//         return JSON.parse(storedUser);
//       } catch (e) {
//         return jwt_decode(token);
//       }
//     }
//     return jwt_decode(token);
//   });
//   const [sidebarStatus, setSidebarStatus] = useState(
//     localStorage.getItem("sidebar"),
//   );
//   const [resetPasswordData, setResetPasswordData] = useState({});
//   const [pageName, setPageName] = useState(
//     localStorage.getItem("pageName")
//       ? localStorage.getItem("pageName")
//       : "Dashboard",
//   );
//   // Add state for profile completion data
//   const [profileCompletion, setProfileCompletion] = useState(null);
//   // --- MFA: tracks an in-progress login that is waiting on a TOTP code ---
//   const [mfaChallenge, setMfaChallenge] = useState(null);
//   const navigate = useNavigate();
//   const notification = useToastContext();
//   const [loginMessage] = useState("Admin details updated successfully.");

//   const updatePageName = (name) => {
//     localStorage.setItem("pageName", name);
//     setPageName(name);
//   };

//   // Function to fetch profile completion status based on user role
//   const fetchProfileCompletionStatus = async (userRole, userId) => {
//     try {
//       let completionApi = "";

//       // Skip for admin users
//       if (userRole === "admin" || userRole === "subadmin") {
//         setProfileCompletion({
//           skip: true,
//           message: "Admin user - no completion check needed",
//         });
//         return;
//       }

//       // Determine the correct API endpoint based on user role
//       switch (userRole) {
//         case "HRAdmin":
//           completionApi = apiPath.hrCompletionStatus;
//           break;
//         case "AccountantAdmin":
//           completionApi = apiPath.accountantCompletionStatus;
//           break;
//         default:
//           console.warn(`No completion API defined for role: ${userRole}`);
//           return;
//       }

//       if (completionApi) {
//         const { status, data } = await apiGet(completionApi);
//         if (status === 200 && data.success) {
//           setProfileCompletion({
//             ...data.results,
//             role: userRole,
//             lastChecked: new Date().toISOString(),
//           });
//         } else {
//           console.error(
//             "Failed to fetch profile completion status:",
//             data.message,
//           );
//           setProfileCompletion({
//             error: true,
//             message: data.message || "Failed to fetch completion status",
//           });
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching profile completion status:", error);
//       setProfileCompletion({
//         error: true,
//         message: "Error fetching completion status",
//       });
//     }
//   };

//   // Shared by loginUser() and completeMfaLogin() so both paths finish
//   // login identically once a real token exists.
//   const finalizeLogin = async (data) => {
//     const token = data?.results?.token || null;
//     const refresh_token = data?.results?.refresh_token || null;
//     localStorage.setItem("token", token);
//     localStorage.setItem("refresh_token", refresh_token);
//     localStorage.setItem("sidebar", "open");
//     setSidebarStatus("open");
//     localStorage.setItem("pageName", "Dashboard");
//     setPageName("Dashboard");

//     // ═══ FIX: Store full user data from results, not just decoded token ═══
//     const fullUserData = { ...data.results };
//     delete fullUserData.token;
//     delete fullUserData.refresh_token;
//     delete fullUserData.recoveryCodes;

//     // Store full user data in localStorage
//     localStorage.setItem("user", JSON.stringify(fullUserData));
//     setUser(fullUserData);

//     // Also decode token for any missing fields
//     const decodedUser = jwt_decode(token);

//     // Merge decoded data with full user data
//     const mergedUser = { ...decodedUser, ...fullUserData };
//     setUser(mergedUser);

//     if (decodedUser.role !== "admin" && decodedUser.role !== "subadmin") {
//       await fetchProfileCompletionStatus(decodedUser.role, decodedUser._id);
//     } else {
//       setProfileCompletion({
//         skip: true,
//         message: "Admin user - no completion check needed",
//       });
//     }

//     notification.success(data.message);
//     navigate("/dashboard");
//   };

//   let loginUser = async (body) => {
//     let api = "";
//     if (location.pathname === "/accountant-login") {
//       api = apiPath.loginAccountant;
//     } else if (location.pathname === "/hr-login") {
//       api = apiPath.loginHR;
//     } else {
//       api = apiPath.loginUser;
//     }

//     document.getElementById("loader").classList.remove("hidden");
//     try {
//     const { status, data } = await apiPost(
//       api,
//       pick(body, ["email","password","latitude","longitude","loginAddress","loginIp","deviceId"])
//     );

//     if (status === 200) {
//       if (data.success) {
//         // --- MFA: password was correct, but this account has MFA enabled.
//         if (data.results?.mfaRequired || data.results?.mfaSetupRequired) {
//           setMfaChallenge({
//             setup: !!data.results.mfaSetupRequired,
//             preAuthToken: data.results.preAuthToken,
//             role: data.results.role,
//             qrCodeDataUrl: data.results.qrCodeDataUrl,
//             manualEntryKey: data.results.manualEntryKey,
//           });
//           document.getElementById("loader").classList.add("hidden");
//           return;
//         }

//         await finalizeLogin(data);
//       } else {
//         notification.error(
//           data?.message ||
//             "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP.",
//         );
//       }
//     }
//     } catch (err) {
//       const data = err?.response?.data;
//       notification.error(
//         data?.message ||
//           "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP.",
//       );
//     }
//     document.getElementById("loader").classList.add("hidden");
//   };

//   // --- MFA: called by the Authenticator modal's onVerify prop during login.
//   const verifyLoginMfa = async (code, { isRecoveryCode } = {}) => {
//     let loginGps = {};
//     try {
//       loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
//     } catch (err) {
//       loginGps = {};
//     }
//     const body = isRecoveryCode
//       ? {
//           preAuthToken: mfaChallenge?.preAuthToken,
//           recoveryCode: code,
//           role: mfaChallenge?.role,
//           ...loginGps,
//         }
//       : {
//           preAuthToken: mfaChallenge?.preAuthToken,
//           token: code,
//           role: mfaChallenge?.role,
//           ...loginGps,
//         };

//     const { data } = await apiPost(apiPath.mfaLoginVerify, body);

//     if (data?.success) {
//       return { verified: true, __loginData: data };
//     }

//     // Backend may issue a fresh preAuthToken
//     if (data?.results?.preAuthToken) {
//       setMfaChallenge((prev) => ({ ...prev, preAuthToken: data.results.preAuthToken }));
//     }
//     return { verified: false, message: data?.message };
//   };

//   // --- MFA: called by Authenticator's onSuccess once verifyLoginMfa()
//   const completeMfaLogin = async (result) => {
//     setMfaChallenge(null);
//     await finalizeLogin(result.__loginData);
//   };

//   const completeMfaSetupLogin = async (code) => {
//     const { data } = await apiPost(apiPath.mfaLoginSetupVerify, {
//       preAuthToken: mfaChallenge?.preAuthToken,
//       token: code,
//       role: mfaChallenge?.role,
//     });
//     if (data?.success) {
//       setMfaChallenge((prev) => ({
//         ...prev,
//         pendingLogin: data,
//       }));
//       return {
//         verified: true,
//         recoveryCodes: data?.results?.recoveryCodes || [],
//       };
//     }
//     if (data?.results?.preAuthToken || data?.data?.preAuthToken) {
//       setMfaChallenge((prev) => ({
//         ...prev,
//         preAuthToken: data.results?.preAuthToken || data.data?.preAuthToken,
//       }));
//     }
//     return { verified: false, message: data?.message };
//   };

//   const finishMfaSetupLogin = async () => {
//     const data = mfaChallenge?.pendingLogin;
//     setMfaChallenge(null);
//     if (data?.success) await finalizeLogin(data);
//   };

//   const cancelMfaLogin = () => {
//     setMfaChallenge(null);
//   };

//   const updateProfile = async (
//     formData,
//     formDataHR,
//     formDataAccountant,
//     setPicture,
//   ) => {
//     let api = "";
//     if (user.role === "admin" || user.role === "subadmin") {
//       api = apiPath.editProfile;
//     } else if (user.role === "HRAdmin") {
//       api = apiPath.updateProfileHR;
//     } else {
//       api = apiPath.updateProfileAccountant;
//     }
//     let ObjData = "";
//     if (user.role === "admin" || user.role === "subadmin") {
//       ObjData = formData;
//     } else if (user.role === "HRAdmin") {
//       ObjData = formDataHR;
//     } else {
//       ObjData = formDataAccountant;
//     }
//     try {
//       const res = await apiPost(api, ObjData);
//       const token = res?.data?.results?.token || null;
//       const refresh_token = res?.data?.results?.refresh_token || null;
//       localStorage.setItem("token", token);
//       notification.success(res?.data?.message);
//       localStorage.setItem("refresh_token", refresh_token);

//       // ═══ FIX: Update full user data after profile update ═══
//       const fullUserData = { ...res.data.results };
//       delete fullUserData.token;
//       delete fullUserData.refresh_token;

//       localStorage.setItem("user", JSON.stringify(fullUserData));
//       setUser(fullUserData);

//       // Refresh profile completion status after profile update
//       if (fullUserData.role !== "admin" && fullUserData.role !== "subadmin") {
//         await fetchProfileCompletionStatus(fullUserData.role, fullUserData._id);
//       }

//       const { message, success } = res.data;
//       if (res && success) {
//         notification.success(message);
//         setPicture({
//           cropperOpen: false,
//           img: null,
//           zoom: 2,
//           croppedImg:
//             res?.data?.results?.profilePic ||
//             res?.data?.results?.profilePicture,
//         });
//       }
//     } catch (err) {
//       console.log("err:", err);
//     }
//   };

//   // Function to manually refresh completion status
//   const refreshCompletionStatus = async () => {
//     if (user && user.role !== "admin" && user.role !== "subadmin") {
//       await fetchProfileCompletionStatus(user.role, user._id);
//     }
//   };

//   const handleSidebar = () => {
//     let s1 = localStorage.getItem("sidebar");
//     localStorage.setItem("sidebar", s1 == "open" ? "close" : "open");
//     setSidebarStatus(s1 == "open" ? "close" : "open");
//   };

//   let logoutUser = () => {
//     let loginPath = "/login"; // default for admin/subadmin

//     if (user?.role === "HRAdmin") {
//       loginPath = "/hr-login";
//     } else if (user?.role === "AccountantAdmin") {
//       loginPath = "/accountant-login";
//     } else if (user?.role === "admin" || user?.role === "subadmin") {
//       loginPath = "/login";
//     }

//     setUser(null);
//     setProfileCompletion(null);
//     localStorage.removeItem("token");
//     localStorage.removeItem("refresh_token");
//     localStorage.removeItem("user"); // ═══ Remove stored user data ═══
//     notification.success("Logout successfully.");
//     navigate(loginPath);
//   };

//   let contextData = {
//     user,
//     loginUser: loginUser,
//     logoutUser: logoutUser,
//     updateProfile: updateProfile,
//     sidebarStatus: sidebarStatus,
//     handleSidebar,
//     pageName,
//     updatePageName,
//     loginMessage: loginMessage,
//     setResetPasswordData,
//     resetPasswordData,
//     // Add profile completion data and refresh function to context
//     profileCompletion,
//     refreshCompletionStatus,
//     // --- MFA additions ---
//     mfaChallenge,
//     verifyLoginMfa,
//     completeMfaLogin,
//     completeMfaSetupLogin,
//     finishMfaSetupLogin,
//     cancelMfaLogin,
//   };

//   return (
//     <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
//   );
// };